import {
  addQueryParamsToUrl,
  isContainQueryParam,
  MobilePageHeader,
  parseHtml,
  useMessageChannelContext,
} from '@healthhub/react-ui';
import { useContext } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { SourceApp } from '@shared/constants/constants';
import {
  ACTION_BACK_PATH,
  APPOINTMENT_BEFORE_CARE_PATH,
  APPOINTMENT_MAKE_NEW_AODTH_PATH,
  APPOINTMENT_PATH,
  FORCE_BACK_PATH,
  MEDREFILL_PUBLIC_PATH,
  NN_TRIAGE_QUESTIONNAIRE,
  PAYMENTS_PUBLIC_PATH,
  PAYMENTS_RECEIPT_PATH,
  ROUTER_BACK_PATH,
} from '@shared/constants/paths';
import { useLayoutViewContext } from '@shared/context';
import { PageTitleContext } from '@shared/context/PageTitleContextWrapper';
import { useExtendedRouter } from '@shared/hooks/useExtendedRouter';
import useMemberIdentifier from '@shared/hooks/useMemberIdentifier';
import { originUrlSelector } from '@shared/stores/origin/originSelector';
import { originAction } from '@shared/stores/origin/originSlice';
import { useAppDispatch, useAppSelector } from '@shared/stores/rootTypes';
import { MESSAGE_ACTION } from '@shared/utils/bridge';

import { VideoConsultProceedSteps } from '@features/Appointment/AppointmentMakeNew/types/makeNew';
import { PaymentMode } from '@features/PaymentsAndFinancial/Payments/types/payments.types';

const PageHeader = () => {
  const { pageTitle } = useContext(PageTitleContext)!;
  const { sendMessageToNative } = useMessageChannelContext();
  const navigateRouter = useNavigate();
  const location = useLocation();
  const { memberIdentifier } = useMemberIdentifier();
  const { sourceApp } = useLayoutViewContext();
  const { actionUrl, backUrl, displayTitle, isHideBackNavButton, isLandingPage, isNavHeaderHide, path } =
    useExtendedRouter();
  const [searchParams] = useSearchParams();

  const originUrl = useAppSelector(originUrlSelector);
  const dispatch = useAppDispatch();

  const handleBackUrl = (mid: null | string) => {
    if (location.pathname === PAYMENTS_RECEIPT_PATH) {
      const mode = new URLSearchParams(location.search).get('mode');

      if (mode === PaymentMode.MobilePublic) {
        navigateRouter(PAYMENTS_PUBLIC_PATH);
        return;
      }
    }

    if (backUrl === MEDREFILL_PUBLIC_PATH && sourceApp === SourceApp.HB) {
      sendMessageToNative(MESSAGE_ACTION.back());
      return;
    }

    if (backUrl === APPOINTMENT_MAKE_NEW_AODTH_PATH) {
      navigateRouter(
        {
          pathname: APPOINTMENT_MAKE_NEW_AODTH_PATH,
          search: memberIdentifier ? `?mid=${memberIdentifier}` : '',
        },
        {
          state: {
            step: VideoConsultProceedSteps.SUMMARY,
          },
        }
      );
      return;
    }

    if (location.pathname.includes(APPOINTMENT_BEFORE_CARE_PATH) && location.search.includes(NN_TRIAGE_QUESTIONNAIRE)) {
      navigateRouter(
        addQueryParamsToUrl(APPOINTMENT_BEFORE_CARE_PATH, {
          mid: memberIdentifier ?? '',
        })
      );
      return;
    }

    // Go to backUrl in mobile when clicking btnBack
    navigateRouter({
      pathname: backUrl,
      search: mid ? `mid=${mid}` : '',
    });
  };

  const handleBackButtonClick = () => {
    const hasForceBackPath = searchParams.get(FORCE_BACK_PATH) === 'true';

    if (hasForceBackPath) {
      sendMessageToNative(MESSAGE_ACTION.back());
      return;
    }

    const mid = searchParams.get('mid');
    const originPath = originUrl[path];

    if (originPath) {
      if (originPath !== ROUTER_BACK_PATH) {
        const isIncludeMid = isContainQueryParam(originPath, 'mid');
        const finalRedirectUrl = isIncludeMid
          ? originPath
          : addQueryParamsToUrl(originPath, {
              mid: memberIdentifier ?? '',
            });
        dispatch(originAction.removeOriginUrl(path));
        navigateRouter(finalRedirectUrl, { replace: true });
        return;
      }

      navigateRouter(-1);
      return;
    }

    if (location.pathname.startsWith('/appointments') && location.pathname.includes('/success')) {
      navigateRouter({ pathname: APPOINTMENT_PATH, ...(mid && { search: `mid=${mid}` }) });
      return;
    }

    if (backUrl) {
      handleBackUrl(mid);
      return;
    }

    if (actionUrl === ACTION_BACK_PATH) {
      sendMessageToNative(MESSAGE_ACTION.back());
      return;
    }

    navigateRouter(-1);
  };

  if (isNavHeaderHide) {
    return null;
  }

  return (
    <MobilePageHeader
      hideBackButton={isLandingPage || isHideBackNavButton}
      onClick={handleBackButtonClick}
      title={pageTitle ?? (parseHtml(displayTitle) as string)}
    />
  );
};

export default PageHeader;
